import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  DatePicker,
  Field,
  FieldLabel,
  InputGroup,
  InputGroupInput,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@beweegbaas/ui";
import { type DateValue } from "@internationalized/date";
import { useSelector } from "@tanstack/react-form";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, isRedirect } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useEffect, useState } from "react";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import {
  FormErrorSummary,
  TanstackFieldError,
  TanstackSubmitErrorSummary,
} from "#/components/form-errors";
import { useAppForm } from "#/modules/util.forms/form";
import { containerStyles, Page } from "#/components/page";
import { searchSchoolByBrin } from "#/modules/admin/admin.functions";
import {
  createSchoolAdminDefaultLicentieType,
  createSchoolAdminFormSchema,
  createSchoolAdminLicentieTypeOptions,
  createSchoolAdminLicentieTypeSchema,
} from "#/modules/admin/schema.create-school-admin";
import { createAdminSchoolFn, generateSchoolAliasFn } from "#/modules/admin/schools.functions";
import { SchoolSearchResultTile } from "#/modules/school/components/school-search-result-tile";
import { getLicentieTypeTranslation } from "#/modules/util.i18n/enum-mapping";
import { m } from "#/paraglide/messages";
import { captureUnexpectedError } from "#/modules/util.errors/capture";
import type { components } from "#/schema";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/admin/schools/add/")({
  component: AdminSchoolAdd,
  errorComponent: SchoolAddError,
});

function SchoolAddError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

type SchoolSearchResult = components["schemas"]["SchoolSearchResponse"];

function AdminSchoolAdd() {
  const { locale, schoolyear } = Route.useParams();
  const createSchool = useServerFn(createAdminSchoolFn);
  const searchByBrin = useServerFn(searchSchoolByBrin);
  const generateAlias = useServerFn(generateSchoolAliasFn);

  const [searchResults, setSearchResults] = useState<SchoolSearchResult[]>([]);
  const [brinInput, setBrinInput] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<DateValue | null>(null);
  const [aliasEdited, setAliasEdited] = useState(false);

  const form = useAppForm({
    defaultValues: {
      name: "",
      alias: "",
      brin: "",
      address: "",
      houseNumber: "",
      postalCode: "",
      city: "",
      phoneNumber: "",
      bestuursnummer: "",
      bestuursnaam: "",
      licentieType: createSchoolAdminDefaultLicentieType,
    },
    validators: { onSubmit: createSchoolAdminFormSchema },
    onSubmit: async ({ value }) => {
      try {
        const result = await createSchool({
          data: {
            locale,
            schoolyear,
            input: {
              ...value,
              endDate: endDate ? endDate.toString() : undefined,
            },
          },
        });
        if (result && ("formError" in result || "fieldErrors" in result)) {
          form.setErrorMap({
            onSubmit: { form: result.formError, fields: result.fieldErrors ?? {} },
          });
        }
      } catch (error) {
        // Success throws a redirect (navigation) inside the server fn — let it propagate.
        if (isRedirect(error)) throw error;
        captureUnexpectedError(error, "admin-school-add");
        form.setErrorMap({
          onSubmit: { form: m["common.something_went_wrong"](), fields: {} },
        });
      }
    },
  });

  // Alias suggestion follows the naam field until the admin types an alias of their own.
  const name = useSelector(form.store, (s) => s.values.name);
  const [debouncedName] = useDebouncedValue(name, { wait: 400 });
  const aliasSuggestion = useQuery({
    queryKey: ["admin-school-alias", debouncedName],
    queryFn: () => generateAlias({ data: { name: debouncedName } }),
    enabled: !aliasEdited && debouncedName.trim().length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
  const suggestedAlias = aliasSuggestion.data?.alias ?? "";

  useEffect(() => {
    if (aliasEdited || !suggestedAlias) return;
    form.setFieldValue("alias", suggestedAlias);
  }, [aliasEdited, form, suggestedAlias]);

  async function handleSearchBrin(e: React.FormEvent) {
    e.preventDefault();
    setIsSearching(true);
    setSearchError(null);
    try {
      const searchResult = await searchByBrin({ data: { searchBrin: brinInput } });
      if (!searchResult) return;
      const { schools } = searchResult;
      setSearchResults(schools);
      if (schools.length === 0) {
        setSearchError(m["brin.api_errors.school_not_found"]());
      }
    } catch (error) {
      captureUnexpectedError(error, "admin-school-brin-search");
      setSearchResults([]);
      setSearchError(m["common.something_went_wrong"]());
    } finally {
      setIsSearching(false);
    }
  }

  function selectSchool(school: SchoolSearchResult) {
    form.setFieldValue("name", school.schoolName ?? "");
    form.setFieldValue("brin", school.brin ?? "");
    form.setFieldValue("address", school.address ?? "");
    form.setFieldValue("houseNumber", school.houseNumber ?? "");
    form.setFieldValue("postalCode", school.postalCode ?? "");
    form.setFieldValue("city", school.city ?? "");
    form.setFieldValue("phoneNumber", school.phoneNumber ?? "");
    form.setFieldValue("bestuursnummer", school.bestuursnummer ?? "");
    form.setFieldValue("bestuursnaam", school.bestuursnaam ?? "");
  }

  return (
    <Page>
      <Page.Header>
        <Page.Title>School toevoegen</Page.Title>
      </Page.Header>
      <Page.Body className={cn(containerStyles({ container: "default" }), "pb-10")}>
        <div className="grid w-full max-w-4xl gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Zoek op BRIN</CardTitle>
              <CardDescription>
                Selecteer een school om het formulier automatisch te vullen.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearchBrin} className="flex items-end gap-3">
                <Field className="grow">
                  <FieldLabel htmlFor="searchBrin">BRIN</FieldLabel>
                  <InputGroup>
                    <InputGroupInput
                      id="searchBrin"
                      name="searchBrin"
                      value={brinInput}
                      onChange={(e) => setBrinInput(e.target.value)}
                    />
                  </InputGroup>
                </Field>
                <Button type="submit" disabled={isSearching}>
                  Zoek
                </Button>
              </form>

              {searchError ? <FormErrorSummary errors={searchError} className="mt-4" /> : null}

              {searchResults.length > 0 ? (
                <div className="mt-4 space-y-2">
                  {searchResults.map((school) => (
                    <SchoolSearchResultTile
                      key={school.brin}
                      school={school}
                      buttonLabel={
                        school.isRegistered
                          ? m["school.add_school.already_linked_cta"]()
                          : "Selecteer"
                      }
                      onButtonClick={() => selectSchool(school)}
                    />
                  ))}
                </div>
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Schoolgegevens</CardTitle>
            </CardHeader>
            <CardContent>
              <form
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  void form.handleSubmit();
                }}
                className="grid grid-cols-1 gap-4 md:grid-cols-2"
              >
                <div className="md:col-span-2">
                  <TanstackSubmitErrorSummary form={form} />
                </div>

                <form.Field
                  name="name"
                  children={(field) => {
                    const isInvalid = !field.state.meta.isValid;
                    return (
                      <Field data-invalid={isInvalid}>
                        <FieldLabel htmlFor={field.name}>{m["common.fields.name"]()}</FieldLabel>
                        <InputGroup>
                          <InputGroupInput
                            id={field.name}
                            name={field.name}
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            aria-invalid={isInvalid}
                          />
                        </InputGroup>
                        <TanstackFieldError field={field} />
                      </Field>
                    );
                  }}
                />
                <form.Field
                  name="alias"
                  children={(field) => (
                    <Field>
                      <FieldLabel htmlFor={field.name}>Alias</FieldLabel>
                      <InputGroup>
                        <InputGroupInput
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          onChange={(e) => {
                            setAliasEdited(true);
                            field.handleChange(e.target.value);
                          }}
                          onBlur={field.handleBlur}
                        />
                      </InputGroup>
                    </Field>
                  )}
                />
                <form.Field
                  name="brin"
                  children={(field) => {
                    const isInvalid = !field.state.meta.isValid;
                    return (
                      <Field data-invalid={isInvalid}>
                        <FieldLabel htmlFor={field.name}>BRIN</FieldLabel>
                        <InputGroup>
                          <InputGroupInput
                            id={field.name}
                            name={field.name}
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            aria-invalid={isInvalid}
                          />
                        </InputGroup>
                        <TanstackFieldError field={field} />
                      </Field>
                    );
                  }}
                />
                <form.Field
                  name="address"
                  children={(field) => {
                    const isInvalid = !field.state.meta.isValid;
                    return (
                      <Field data-invalid={isInvalid}>
                        <FieldLabel htmlFor={field.name}>Adres</FieldLabel>
                        <InputGroup>
                          <InputGroupInput
                            id={field.name}
                            name={field.name}
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            aria-invalid={isInvalid}
                          />
                        </InputGroup>
                        <TanstackFieldError field={field} />
                      </Field>
                    );
                  }}
                />
                <form.Field
                  name="houseNumber"
                  children={(field) => {
                    const isInvalid = !field.state.meta.isValid;
                    return (
                      <Field data-invalid={isInvalid}>
                        <FieldLabel htmlFor={field.name}>Huisnummer</FieldLabel>
                        <InputGroup>
                          <InputGroupInput
                            id={field.name}
                            name={field.name}
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            aria-invalid={isInvalid}
                          />
                        </InputGroup>
                        <TanstackFieldError field={field} />
                      </Field>
                    );
                  }}
                />
                <form.Field
                  name="postalCode"
                  children={(field) => {
                    const isInvalid = !field.state.meta.isValid;
                    return (
                      <Field data-invalid={isInvalid}>
                        <FieldLabel htmlFor={field.name}>Postcode</FieldLabel>
                        <InputGroup>
                          <InputGroupInput
                            id={field.name}
                            name={field.name}
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            aria-invalid={isInvalid}
                          />
                        </InputGroup>
                        <TanstackFieldError field={field} />
                      </Field>
                    );
                  }}
                />
                <form.Field
                  name="city"
                  children={(field) => {
                    const isInvalid = !field.state.meta.isValid;
                    return (
                      <Field data-invalid={isInvalid}>
                        <FieldLabel htmlFor={field.name}>Plaats</FieldLabel>
                        <InputGroup>
                          <InputGroupInput
                            id={field.name}
                            name={field.name}
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            aria-invalid={isInvalid}
                          />
                        </InputGroup>
                        <TanstackFieldError field={field} />
                      </Field>
                    );
                  }}
                />
                <form.Field
                  name="phoneNumber"
                  children={(field) => {
                    const isInvalid = !field.state.meta.isValid;
                    return (
                      <Field data-invalid={isInvalid}>
                        <FieldLabel htmlFor={field.name}>Telefoon</FieldLabel>
                        <InputGroup>
                          <InputGroupInput
                            id={field.name}
                            name={field.name}
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            onBlur={field.handleBlur}
                            aria-invalid={isInvalid}
                          />
                        </InputGroup>
                        <TanstackFieldError field={field} />
                      </Field>
                    );
                  }}
                />
                <form.Field
                  name="bestuursnummer"
                  children={(field) => (
                    <Field>
                      <FieldLabel htmlFor={field.name}>Bestuursnummer</FieldLabel>
                      <InputGroup>
                        <InputGroupInput
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                        />
                      </InputGroup>
                    </Field>
                  )}
                />
                <form.Field
                  name="bestuursnaam"
                  children={(field) => (
                    <Field>
                      <FieldLabel htmlFor={field.name}>
                        {m["admin.school.bestuursnaam"]()}
                      </FieldLabel>
                      <InputGroup>
                        <InputGroupInput
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                        />
                      </InputGroup>
                    </Field>
                  )}
                />
                <form.Field
                  name="licentieType"
                  children={(field) => (
                    <Field>
                      <FieldLabel>{m["admin.scholen.table.licentietype"]()}</FieldLabel>
                      <Select
                        value={field.state.value}
                        onValueChange={(value) => {
                          const licentieType = createSchoolAdminLicentieTypeSchema.safeParse(value);
                          if (licentieType.success) field.handleChange(licentieType.data);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue>{getLicentieTypeTranslation(field.state.value)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {createSchoolAdminLicentieTypeOptions.map((type) => (
                              <SelectItem key={type} value={type}>
                                {getLicentieTypeTranslation(type)}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                />
                <Field>
                  <FieldLabel>{m["admin.scholen.table.einddatum"]()}</FieldLabel>
                  <DatePicker
                    aria-label="Einddatum"
                    hideTimeZone
                    value={endDate}
                    onChange={setEndDate}
                  />
                </Field>

                <div className="md:col-span-2">
                  <form.Subscribe
                    selector={(state) => state.isSubmitting}
                    children={(isSubmitting) => (
                      <Button type="submit" isPending={isSubmitting}>
                        School toevoegen
                      </Button>
                    )}
                  />
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </Page.Body>
    </Page>
  );
}
